import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import SydButton from '../../../../../../../commonDesign/Button'
import Icon from '../../../../../../../atoms/Icon'
import { useEvalRunForm } from '../../EvalRunFormContext'
import { useProposalCardStyles, useAutoApplyEffect } from './cardCommon'
import ProposalReviewModal from './ProposalReviewModal'

const PROPOSAL_NAME = 'remove_metric'

function RemoveMetricCard ({ toolCallId, args, onApply, autoAccept }) {
  const classes = useProposalCardStyles()
  const { metrics, appliedProposalIds } = useEvalRunForm()
  const isApplied = !!toolCallId && appliedProposalIds.has(toolCallId)

  const [state, setState] = useState(isApplied ? 'applied' : 'pending')
  const [showModal, setShowModal] = useState(false)

  const currentMetric = useMemo(() => (
    metrics.find(m => m.name === args?.name) || null
  ), [metrics, args?.name])

  const apply = useCallback(({ auto = false } = {}) => {
    if (onApply) onApply({ tool: PROPOSAL_NAME, toolCallId, args })
    setState(auto ? 'auto-applied' : 'applied')
    setShowModal(false)
  }, [onApply, toolCallId, args])

  useAutoApplyEffect({ autoAccept, state, apply })

  if (state === 'applied' || state === 'auto-applied') {
    return (
      <div className={`${classes.root} ${classes.applied}`}>
        <div className={classes.appliedRow}>
          <Icon name='check' customSize='16px' />
          <span>{state === 'auto-applied' ? 'Metric auto-removed' : 'Metric removed'}: {args.name}</span>
        </div>
      </div>
    )
  }

  if (state === 'dismissed') {
    return (
      <div className={`${classes.root} ${classes.dismissed}`}>
        <div className={classes.dismissedRow}>
          Removal dismissed: {args.name}
        </div>
      </div>
    )
  }

  const notFound = !currentMetric

  return (
    <>
      <div className={classes.root}>
        <div className={classes.header}>
          <span className={classes.title}>Proposed metric removal</span>
        </div>
        <div className={classes.summary}>
          {notFound
            ? `No metric named ${args.name} (already removed?)`
            : `Remove metric ${args.name}`}
        </div>
        {args.reasoning && <div className={classes.reasoning}>{args.reasoning}</div>}
        <div className={classes.divider} />
        <div className={classes.actions}>
          <SydButton size='xs' variant='outline' onClick={() => setState('dismissed')}>
            Dismiss
          </SydButton>
          {onApply && !notFound && (
            <SydButton size='xs' variant='ai' onClick={() => setShowModal(true)}>
              Review
            </SydButton>
          )}
        </div>
      </div>

      {showModal && (
        <ProposalReviewModal
          flavor='metric'
          title={`Review removal: ${args.name}`}
          reasoning={args.reasoning}
          metricCurrent={currentMetric}
          metricProposed={null}
          metricMode='remove'
          onAccept={() => apply()}
          onReject={() => setShowModal(false)}
        />
      )}
    </>
  )
}

RemoveMetricCard.propTypes = {
  toolCallId: PropTypes.string,
  args: PropTypes.shape({
    name: PropTypes.string.isRequired,
    reasoning: PropTypes.string
  }).isRequired,
  onApply: PropTypes.func,
  autoAccept: PropTypes.bool
}

export default RemoveMetricCard
